import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS } from "@shared/constants/fields";
import { screenByKey } from "@shared/interview/chapters";
import { boardCell, boardOptions, boardPickNotes, boardStep, boardTable, boardTiles, cellRuledOff } from "@shared/interview/board";
import { EXECUTOR_ROLE, isAlternateRole, type WillNamesBackup } from "@shared/constants/executor-roles";
import { DRAWN_CELLS } from "@shared/interview/board-map";
import { useSectionRows, type SectionRow } from "./useSectionRows";
import { saveInBackground, useSaveFailure } from "./saveFailure";
import { TilePicker, type TileSelection } from "./TilePicker";
import { InterviewRowTable, type RowTableRow } from "./InterviewRowTable";
import { ContactCard, LinkedPersonCell, cardSummary, type CardSlot } from "./PersonPicker";
import { ConsequenceNote, ConsequencePicker } from "./ConsequencePicker";
import { NoteCard, OffCell, PickerCell } from "./InterviewCells";
import { OnePanelProvider, usePanels } from "./useOnePanelOpen";
import { ownListKey } from "@/components/plan/useOwnList";

/**
 * s223 — YOUR EXECUTOR (SPEC: `Main.dc.html`, the `exec` step; D-222.10 part 3).
 *
 * Two tiles — "Named, and they know" and "Named, but I have not told them" — and "I have not chosen one yet".
 * One row per executor in `legal-your-executor`; more than one can be true (a co-executor, an alternate).
 *
 * ## The tile IS the row's standing — stored (`told`), never inferred
 * A pick starts a row carrying that standing. A named row with NO standing (added somewhere else: an advisor, the
 * Your Executor page, an older page) is not dropped: s226 (board v331 `noStanding`, Reade option b) — its standing
 * cell is a dropdown on THAT ROW ONLY, offering the tiles' two standings, and choosing one writes `told`, the same
 * field and the same value a tile writes. A tile pick no longer gives such rows its standing (Reade, Q-A): ticking
 * a tile starts a row, as the board declares, and the row's own dropdown is the only fix.
 *
 * ## 🔴 The executor-access sync is left EXACTLY as it is
 * `POST /generic-entries` on this section runs `syncExecutorAccessFromRow` (entries-routes.ts), which creates or
 * links the auth row and sends NO email — the Send invitation button is the sole trigger, so the board's
 * "Nothing is sent yet" is literally true. This screen writes through that same route and adds nothing to it.
 *
 * ## s232 — the backup (BACKUP-NUDGE-1 PR 1; D-231.14, D-232.8, D-232.13, D-232.16)
 * "Does your will name a backup executor?" sits on the FIRST executor's row (other rows: "not asked here"); the
 * answer is the plan's, on `clients.will_names_backup` via situation-flags. Yes starts a Backup row — a
 * `legal-your-executor` row with role Alternate and no standing; it reads "Backup", or "Backup 1", "Backup 2". No /
 * Not sure hides the backups (never deletes). A backup gets no access row on save (the sync holds it until "Ask
 * Michael" exists, PR 2).
 *
 * ## s233 — the backup's told choice (D-232.16; board 1 v417, `backupStanding`)
 * A backup row's lead is its OWN dropdown, worded with the row's label: "Backup 1, and they know" / "Backup 1, not
 * told yet". It stores `told` — the same field and the same two values as an executor's standing (a backup has no
 * access row, so the value stays in the row's cell until one exists). It is never a tile and never the no-standing
 * picker: choosing it picks no tile. "not told yet" shows the board's note under the row, and the one list's "Ask …
 * to be your backup executor" row (shared/plan-home/own-list.ts, `tellbk`) keeps that note's promise.
 *
 * ## s232 (PR 2): the two "Added to the list on your plan's home" promises are KEPT, so both notes show
 * The No / Not sure note (its reason under Update your will) and the not-told tile's note (its "Ask … to be your
 * executor" row) — the one list (shared/plan-home/own-list.ts) holds each. ADDED-FOR-YOU-1's to-do half.
 *
 * Deselect / "none" / × HIDE rows (isHidden, no value) and never delete — the children screens' rule (G8).
 */

const EXEC = boardStep("exec");
const EXEC_TILES = boardTiles(EXEC);
/** s231 (D-231.80): the standings are the board's tiles LESS its row-only ones — the board's own rule (its standing
 *  picker filters `rowOnly`). The Backup tile is a row kind BACKUP-ACTIVATION-1 builds; it is never a standing. */
export const STANDING_TILES = EXEC_TILES.tiles.filter((t) => !t.rowOnly);
const TILES = STANDING_TILES.map(({ id, label, sub, mark, color }) => ({ id, label, sub, mark, color }));
/** Tile id → the stored standing: the tile's own words (the `told` field's choices are the tile labels). */
export const STANDING: Readonly<Record<string, string>> = Object.fromEntries(STANDING_TILES.map((t) => [t.id, t.label]));
const TILE_OF_STANDING = new Map(Object.entries(STANDING).map(([id, v]) => [v, id]));

/** s232 (D-231.14, D-232.16): the board's row-only Backup tile — a row KIND, never a standing. Its row is added by
 *  answering Yes to the backup question; it reads "Backup", or "Backup 1", "Backup 2" once there are two. */
export const BACKUP_TILE = EXEC_TILES.tiles.find((t) => t.rowOnly) ?? (() => { throw new Error('board: "exec" declares no row-only backup tile'); })();

const CELL = { who: boardCell(EXEC, "who"), rel: boardCell(EXEC, "rel"), bk: boardCell(EXEC, "bk") };
/** s226 — the board's `noStanding` (the picker's empty word). Required: the screen asks for a standing it cannot word. */
const NO_STANDING = EXEC.noStanding ?? (() => { throw new Error('board: "exec" declares no noStanding'); })();
/** The standings a row with none may take: the tiles' own ids and words, in the board's order. */
export const STANDING_OPTIONS = TILES.map((t) => ({ value: t.id, label: t.label }));
export const EXEC_RELATIONSHIPS = boardOptions("exec", CELL.rel);
// s232 (D-232.22): the backup column (`bk`) is BUILT — DRAWN_CELLS no longer lists it, so the table carries it.
export const EXEC_TABLE = boardTable(EXEC, DRAWN_CELLS.exec ?? []);

/** s232 (D-232.8) — "Does your will name a backup executor?": the board's words, the enum's stored values. */
export const BACKUP_OPTIONS = boardOptions("exec", CELL.bk);
/**
 * s232 (PR 2) — the board's notes, whole. The No / Not sure note's "Added to the list on your plan's home." was HELD
 * until the one list existed (ADDED-FOR-YOU-1); it does now — "no backup executor named" under Update your will
 * (shared/plan-home/own-list.ts) — so the promise is kept and the sentence is back.
 */
export const BACKUP_NOTES: Record<string, string> = boardPickNotes("exec", "bk");
/** s232 (PR 2, ADDED-FOR-YOU-1's to-do half): the not-told tile's note — "Added to the list on your plan's home: tell
 *  them…". The one list's "Ask … to be your executor" row keeps that promise for every not-told executor. */
export const UNTOLD_NOTE = EXEC.noteFor?.untold ?? "";

/** s233 (D-232.16): a backup row's own told choice — the board's `backupStanding`. Required: the screen cannot word it. */
export const BACKUP_STANDING = EXEC.backupStanding ?? (() => { throw new Error('board: "exec" declares no backupStanding'); })();
/** The backup's two choices, in the board's order, onto the two standings (the same stored `told` values): "and they
 *  know" is the knows tile's, "not told yet" the untold tile's. Each is worded after the row's own label. Pure. */
export function backupStandingOptions(lead: string): { value: string; label: string }[] {
  return BACKUP_STANDING.opts.map((o, i) => ({ value: STANDING_TILES[i].id, label: `${lead}, ${o}` }));
}
/** The picker's empty word after the row's label, as the board draws it: "Backup 1 · Have you told them?". Pure. */
export const backupStandingPlaceholder = (lead: string) => `${lead} · ${BACKUP_STANDING.ph}`;
/** s233 (D-233.14): a named executor's standing dropdown — the board's `execStanding`. s233 (D-233.49 point 2): its words are
 *  its own ("Named, not told yet"), one per standing in the tiles' order; the STORED value stays the tile's words (`told`'s
 *  choices), so a row saved with "Named, but I have not told them" still shows, and saves, as "Named, not told yet". */
export const EXEC_STANDING = EXEC.execStanding ?? (() => { throw new Error('board: "exec" declares no execStanding'); })();
if (EXEC_STANDING.opts.length !== STANDING_TILES.length) {
  throw new Error(`board: execStanding offers ${EXEC_STANDING.opts.length} choices for ${STANDING_TILES.length} standings`);
}
export const EXEC_STANDING_OPTIONS = EXEC_STANDING.opts.map((label, i) => ({ value: STANDING_TILES[i].id, label }));
/** The not-told choice's tile — its mark colour is the board's for "not told yet". */
const UNTOLD_TILE = STANDING_TILES.find((t) => t.id === "untold");

/** The tile a row belongs to, or null when it carries no standing. A backup (role Alternate, s232 D-232.16) is the
 *  Backup tile whatever its `told` — it never takes the no-standing picker and never becomes an ordinary executor. Pure. */
export function tileOfRow(r: Pick<SectionRow, "values">): string | null {
  if (isAlternateRole(r.values[FIELD_KEYS.ROLE])) return BACKUP_TILE.id;
  return TILE_OF_STANDING.get(r.values[FIELD_KEYS.TOLD] ?? "") ?? null;
}

/** The row's lead words: a standing tile's label, or the backup's — numbered once there are two (D-231.28(a)). Pure. */
export function leadOf(tileId: string | null, backupIndex: number, backupCount: number): string {
  if (tileId === BACKUP_TILE.id) return backupCount > 1 ? `${BACKUP_TILE.label} ${backupIndex + 1}` : BACKUP_TILE.label;
  return TILES.find((t) => t.id === tileId)?.label ?? "";
}

/** A row names an executor once it carries a name. Pure. */
export function hasExecName(r: Pick<SectionRow, "values">): boolean {
  return (r.values[FIELD_KEYS.NAME] ?? "").trim() !== "";
}

/** The executor's card: one person, the row's own name, phone, email and address. */
const WHO_SLOTS: CardSlot[] = [{
  label: "Person 1", name: { kind: "text", key: FIELD_KEYS.NAME },
  phone: FIELD_KEYS.PHONE, email: FIELD_KEYS.EMAIL, address: FIELD_KEYS.ADDRESS,
}];

export function ExecScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  return (
    <OnePanelProvider>
      <ExecScreenBody clientId={clientId} onAnswered={onAnswered} />
    </OnePanelProvider>
  );
}

function ExecScreenBody({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const data = useSectionRows(clientId, CATEGORY_SLUGS.LEGAL, SECTION_SLUGS.LEGAL_YOUR_EXECUTOR, onAnswered, { typed: true });
  const { rows, fieldIdByKey } = data;
  const panels = usePanels();
  // Creating a row is Class A (a second fire is a second executor) — ONE lock for every path that creates one.
  const createFlight = useSingleFlight();
  const [createdHere, setCreatedHere] = useState<ReadonlySet<string>>(() => new Set());
  const create = (tileId: string) =>
    createFlight.run(() => data.createRow({ [FIELD_KEYS.TOLD]: STANDING[tileId] }))
      .then((id) => { if (id) setCreatedHere((s) => new Set(s).add(id)); });
  // s232 (D-231.14): a Backup row is an executor row with role Alternate, started with no told choice — the planner
  // makes it on the row's own dropdown (s233, D-232.16). The same lock: a second fire is a second backup.
  const createBackup = () =>
    createFlight.run(() => data.createRow({ [FIELD_KEYS.ROLE]: EXECUTOR_ROLE.ALTERNATE }))
      .then((id) => { if (id) setCreatedHere((s) => new Set(s).add(id)); });

  // s232 (D-232.8): the answer is the PLAN's — clients.will_names_backup, read from the client and saved as a flag.
  const { data: client } = useQuery<{ willNamesBackup?: WillNamesBackup | null }>({ queryKey: ["/api/clients", clientId], enabled: !!clientId });
  const onSaveFailed = useSaveFailure();
  const flagMutation = useMutation({
    mutationFn: async (flags: { willNamesBackup: WillNamesBackup | null }) =>
      await apiRequest("PATCH", `/api/clients/${clientId}/situation-flags`, flags),
    onError: onSaveFailed,
  });
  const [backupAnswer, setBackupAnswer] = useState<string | null>(null);
  const bk = backupAnswer ?? client?.willNamesBackup ?? "";
  // s232 (D-232.78, cause D): the answers save IN ORDER — one queue, each answer's saves start only after the previous
  // answer's have finished, and an answer a newer one has replaced is skipped. So the last answer wins whatever order
  // the network finishes in (a slow No can no longer land after a Yes). Each run reads the CURRENT rows (a ref), not
  // the rows of the render that queued it.
  const rowsNow = useRef(rows);
  rowsNow.current = rows;
  const answerQueue = useRef<Promise<unknown>>(Promise.resolve());
  const latestAnswer = useRef(0);
  /** Yes brings the backup rows back, or starts the first; No / Not sure hides them (G8: hide, never delete — the
   *  tiles' own rule), so a later Yes restores who was named. */
  const answerBackup = (value: string): Promise<void> => {
    const answer = value as WillNamesBackup;
    const turn = ++latestAnswer.current;
    setBackupAnswer(answer);
    const run = async () => {
      if (turn !== latestAnswer.current) return; // replaced by a later answer, which runs next
      await flagMutation.mutateAsync({ willNamesBackup: answer });
      void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId] });
      const backups = rowsNow.current.filter((r) => tileOfRow(r) === BACKUP_TILE.id);
      if (answer === "yes") {
        if (backups.length === 0) { await createBackup(); return; }
        // Every backup row, not only those this render saw hidden — a queued Yes may run after a No it never saw.
        for (const r of backups) await data.setRowHidden(r.rowId, false);
        data.refresh();
      } else {
        for (const r of backups) await data.setRowHidden(r.rowId, true);
        data.refresh();
      }
    };
    const done = answerQueue.current.then(run);
    answerQueue.current = done.catch(() => {}); // a refused save shows its pop-up (the caller) and never stalls the queue
    return done;
  };

  const visible = rows.filter((r) => !r.hidden);
  const [selection, setSelection] = useState<TileSelection | null>(null);
  // Seeded ONCE from what the plan holds, then owned here — a refetch mid-answer must not flip a pressed tile.
  useEffect(() => {
    if (selection || !data.ready) return;
    const selected = TILES.map((t) => t.id).filter((id) => visible.some((r) => hasExecName(r) && tileOfRow(r) === id));
    setSelection({ selected, none: false });
  }, [selection, data.ready, visible]);
  const sel = selection ?? { selected: [], none: false };

  const turnOn = async (tileId: string) => {
    const ofTile = rows.filter((r) => tileOfRow(r) === tileId);
    if (ofTile.some((r) => r.hidden)) {
      for (const r of ofTile.filter((x) => x.hidden)) await data.setRowHidden(r.rowId, false);
      data.refresh();
    } else if (ofTile.length === 0) {
      await create(tileId);
    } else if (!ofTile.some(hasExecName)) {
      // Only a blank row of this standing (a tap never named): draw it as the starter rather than start another.
      setCreatedHere((s) => new Set(s).add(ofTile[ofTile.length - 1].rowId));
    }
  };
  /** A row with no standing takes one from its own dropdown: the tile's `told`, and the tile shows as picked so the row
   *  (now of that tile) stays drawn. Starts no row. */
  const setStanding = async (rowId: string, tileId: string) => {
    await data.writeCells(rowId, { [FIELD_KEYS.TOLD]: STANDING[tileId] });
    setSelection((s) => {
      const cur = s ?? { selected: [], none: false };
      return cur.selected.includes(tileId) ? cur : { selected: [...cur.selected, tileId], none: false };
    });
  };
  /** s233 (D-232.16): a backup's told choice — `told` on THAT row only. It picks no tile (a backup is never a
   *  standing), and the plan's home list follows it, so its cached list is marked stale. */
  const setBackupStanding = async (rowId: string, tileId: string) => {
    await data.writeCells(rowId, { [FIELD_KEYS.TOLD]: STANDING[tileId] });
    void queryClient.invalidateQueries({ queryKey: ownListKey(clientId) });
  };
  /** s233 (D-233.14): a named executor's standing, changed in place — the no-standing picker's write, and the home's
   *  one list follows it ("Ask … to be your executor" comes and goes with "not told"). */
  const changeStanding = async (rowId: string, tileId: string) => {
    await setStanding(rowId, tileId);
    void queryClient.invalidateQueries({ queryKey: ownListKey(clientId) });
  };
  const turnOff = async (tileId: string) => {
    for (const r of rows.filter((x) => tileOfRow(x) === tileId && !x.hidden)) await data.setRowHidden(r.rowId, true);
    data.refresh();
  };

  const onTiles = async (next: TileSelection, changed: string) => {
    setSelection(next);
    if (changed === "none") {
      if (!next.none) return;
      // "I have not chosen one yet": nothing is stored for the answer itself; the rows it replaces are hidden.
      for (const r of visible) await data.setRowHidden(r.rowId, true);
      data.refresh();
      return;
    }
    if (next.selected.includes(changed)) await turnOn(changed);
    else await turnOff(changed);
  };

  // × HIDES the row and leaves "Name removed · Undo" in its place for this visit (G8).
  const [removedHere, setRemovedHere] = useState<ReadonlyMap<string, string>>(() => new Map());
  const removeRow = async (rowId: string) => {
    const r = rows.find((x) => x.rowId === rowId);
    setRemovedHere((m) => new Map(m).set(rowId, (r?.values[FIELD_KEYS.NAME] ?? "").trim() || "This person")); // the children screens' own words
    try {
      await data.setRowHidden(rowId, true);
    } catch (err) {
      // s228 (ruling 4): a refused × puts the row back — never "… removed · Undo" for a removal that did not happen.
      setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
      throw err;
    }
    data.refresh();
  };
  const undoRemove = async (rowId: string) => {
    await data.setRowHidden(rowId, false);
    setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
    data.refresh();
  };

  // The rows drawn: a picked tile's rows (named, or started this visit), rows removed this visit (their undo
  // line), and named rows with no standing yet. A blank row loaded from the plan is not a person (fix 7).
  const drawn = rows.filter((r) => {
    if (removedHere.has(r.rowId)) return true;
    if (r.hidden) return false;
    const tile = tileOfRow(r);
    if (tile === null) return hasExecName(r);
    // s232: a backup row is drawn on its own — it belongs to no tile the planner ticks.
    if (tile === BACKUP_TILE.id) return hasExecName(r) || createdHere.has(r.rowId);
    return sel.selected.includes(tile) && (hasExecName(r) || createdHere.has(r.rowId));
  });
  // s232 (D-231.14): the backup question sits on the FIRST executor's row; every other row reads "not asked here".
  // s232 (D-232.71, cause B): the first executor row that has a NAME; a blank starter row only when no row has one.
  const execRows = drawn.filter((r) => !removedHere.has(r.rowId) && tileOfRow(r) !== BACKUP_TILE.id);
  const firstExecRowId = (execRows.find(hasExecName) ?? execRows[0])?.rowId ?? null;
  const backupIds = drawn.filter((r) => !removedHere.has(r.rowId) && tileOfRow(r) === BACKUP_TILE.id).map((r) => r.rowId);

  const table: RowTableRow[] = drawn.map((r) => {
    const tileId = tileOfRow(r);
    const tile = tileId === BACKUP_TILE.id ? BACKUP_TILE : TILES.find((t) => t.id === tileId);
    const lead = leadOf(tileId, backupIds.indexOf(r.rowId), backupIds.length);
    if (removedHere.has(r.rowId)) {
      return { rowId: r.rowId, lead, cells: [], removed: { label: removedHere.get(r.rowId)!, onUndo: () => saveInBackground(undoRemove(r.rowId)) } };
    }
    const v = r.values;
    const tid = (what: string) => `interview-exec-${what}-${r.rowId}`;
    const whoKey = `${r.rowId}:who`;
    const noteKey = `${r.rowId}:note`;
    const headerOf = (k: string) => EXEC_TABLE.columns.find((c) => c.key === k)?.label ?? "";
    return {
      rowId: r.rowId,
      lead,
      ...(tile?.mark && tile.color ? { leadMark: { mark: tile.mark, color: tile.color } } : {}),
      // s226 (board v331 `noStanding`): no standing yet — the lead asks for it, on this row only. Never blank.
      ...(tileId === null ? {
        // s233 (D-233.49 point 3): the named row's own list and words — ONE list, ONE placeholder ("Their standing").
        leadCell: <PickerCell value="" options={EXEC_STANDING_OPTIONS} placeholder={NO_STANDING.ph} ariaLabel={headerOf("lead")}
          testId={tid("standing")} onCommit={(id) => { if (STANDING[id]) saveInBackground(setStanding(r.rowId, id)); }} />,
      } : {}),
      // s233 (D-233.14): a NAMED executor's standing is a dropdown of the same two choices — changeable in place, the
      // same `told` write as the no-standing picker. A row not yet named keeps the tile's words (the tiles add rows).
      ...(tileId !== null && tileId !== BACKUP_TILE.id && hasExecName(r) ? {
        leadCell: <PickerCell value={tileId} options={EXEC_STANDING_OPTIONS} placeholder={EXEC_STANDING.ph} ariaLabel={headerOf("lead")}
          testId={tid("estanding")} onCommit={(id) => { if (STANDING[id] && id !== tileId) saveInBackground(changeStanding(r.rowId, id)); }} />,
      } : {}),
      // s233 (D-232.16): a backup's lead is its own told choice, worded with the row's label.
      ...(tileId === BACKUP_TILE.id ? {
        leadCell: <PickerCell value={TILE_OF_STANDING.get(v[FIELD_KEYS.TOLD] ?? "") ?? ""} options={backupStandingOptions(lead)}
          placeholder={backupStandingPlaceholder(lead)} ariaLabel={headerOf("lead")} testId={tid("bstanding")}
          onCommit={(id) => { if (STANDING[id]) saveInBackground(setBackupStanding(r.rowId, id)); }} />,
      } : {}),
      cells: [
        <LinkedPersonCell key="who" value="" label={cardSummary(WHO_SLOTS, v)} word={CELL.who.word ?? ""}
          open={panels.isOpen(whoKey)} onOpen={() => panels.toggle(whoKey)} testId={tid("who")} />,
        <ConsequencePicker key="rel" value={v[FIELD_KEYS.RELATIONSHIP] ?? ""} options={EXEC_RELATIONSHIPS} placeholder={CELL.rel.ph}
          ariaLabel={headerOf("rel")} testId={tid("rel")} onCommit={(val) => saveInBackground(data.writeCells(r.rowId, { [FIELD_KEYS.RELATIONSHIP]: val }))} />,
        r.rowId === firstExecRowId && !cellRuledOff(EXEC, tileId, "bk")
          ? <ConsequencePicker key="bk" value={bk} options={BACKUP_OPTIONS} placeholder={CELL.bk.ph} ariaLabel={headerOf("bk")} testId={tid("bk")}
              onCommit={(val) => saveInBackground(answerBackup(val))} />
          : <OffCell key="bk" testId={tid("bk-off")} />,
      ],
      // "more than one can be true" — a second executor of the same standing is a second row; a second backup likewise.
      canAddRow: !!tileId,
      // s226 (CH1-ROW-NOTES-COLUMN-1): the board draws Note on every row (Main.dc.html, the row actions).
      note: {
        value: v[FIELD_KEYS.NOTES] ?? "",
        open: panels.isOpen(noteKey),
        onToggle: () => panels.toggle(noteKey),
        // s237 (D-237.30): the save is returned, so the Note's Done waits for it.
        onCommit: (val) => (val !== (v[FIELD_KEYS.NOTES] ?? "") ? data.writeCells(r.rowId, { [FIELD_KEYS.NOTES]: val }) : undefined),
      },
      panels: (
        <>
          {panels.isOpen(whoKey) && (
            <ContactCard interviewLook key={whoKey} title={CELL.who.title ?? ""} slots={WHO_SLOTS} values={v} fieldIdByKey={fieldIdByKey} clientId={clientId}
              undo={CELL.who.undo} onSave={(c) => data.writeCells(r.rowId, c)} onClose={() => panels.close(whoKey)} testId={tid("card")} />
          )}
          {r.rowId === firstExecRowId && <ConsequenceNote notes={BACKUP_NOTES} value={bk} testId={tid("bk-note")} />}
          {tileId === BACKUP_TILE.id && v[FIELD_KEYS.TOLD] === STANDING.untold && (
            <NoteCard mark={BACKUP_TILE.mark ?? ""} color={UNTOLD_TILE?.color ?? ""} testId={tid("bstanding-note")}>{BACKUP_STANDING.untoldNote}</NoteCard>
          )}
        </>
      ),
    };
  });

  if (data.ready && !fieldIdByKey.has(FIELD_KEYS.TOLD)) {
    // Honest failure: until the template carries `told` (it converges on deploy), a pick could not be stored.
    return (
      <p className="text-[14px] text-destructive" data-testid="interview-exec-not-ready">
        This part of your plan isn't ready yet. Please try again shortly.
      </p>
    );
  }

  return (
    <div data-testid="interview-exec">
      <TilePicker compact tiles={TILES} selection={sel} onChange={(n, c) => saveInBackground(onTiles(n, c))} none={screenByKey("exec")?.none}
        columns={EXEC_TILES.cols as 2 | 3 | 4 | 5} testIdPrefix="interview-exec" />
      <InterviewRowTable rowMarks strongRowLine {...EXEC_TABLE} rows={table}
        onAddRow={(rowId) => {
          const t = tileOfRow(rows.find((r) => r.rowId === rowId) ?? { values: {} });
          if (t === BACKUP_TILE.id) saveInBackground(createBackup());
          else if (t) saveInBackground(create(t));
        }}
        onRemove={(id) => saveInBackground(removeRow(id))} testIdPrefix="interview-exec-table" cutLead />
      {/* s232 (PR 2): the not-told tile's note, the pets screen's way — one card when the tile is picked. */}
      {UNTOLD_NOTE && sel.selected.includes("untold") && (() => {
        const t = TILES.find((x) => x.id === "untold");
        return (
          <div className="mt-6 flex flex-col gap-1.5">
            <NoteCard mark={t?.mark ?? ""} color={t?.color ?? ""} testId="interview-exec-note-untold">{UNTOLD_NOTE}</NoteCard>
          </div>
        );
      })()}
    </div>
  );
}
